// Al inicio:
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Paper,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material"
import { Circle, AdminPanelSettings, Person } from "@mui/icons-material"
import { ProfileDto } from "../../types/UserToken"
import { ChannelMemberDto } from "../../types/channelMember"
import { ChannelDto } from "../../types/channel"

export interface EnrichedChannelMember extends ChannelMemberDto {
  profile: ProfileDto
}

interface ActiveUsersSidebarProps {
  members: EnrichedChannelMember[]
  currentUser: ProfileDto
  isGlobal: boolean
  selectedChannel?: ChannelDto | null
  globalActiveUsers?: ProfileDto[]
  onlineMembers?: EnrichedChannelMember[]
  offlineMembers?: EnrichedChannelMember[]
}

export default function ActiveUsersSidebar({
  members,
  isGlobal,
  currentUser,
  globalActiveUsers = [],
  onlineMembers = [],
  offlineMembers = [],
}: ActiveUsersSidebarProps) {
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"))

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        borderRadius: 0,
        borderLeft: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          p: 2,
        }}
      >
        <Typography
          variant={isSmallScreen ? "subtitle1" : "h6"}
          sx={{ fontWeight: 600, mb: 1 }}
        >
          {isGlobal ? "Usuarios en línea" : "Miembros del canal"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isGlobal
            ? `${globalActiveUsers.length} usuarios conectados`
            : `${onlineMembers.length} en línea • ${members.length} total`}
        </Typography>
      </Box>

      {/* Users List */}
      <Box sx={{ flexGrow: 1, overflow: "auto" }}>
        {isGlobal ? (
          <List sx={{ p: 1 }}>
            {globalActiveUsers.map((member) => {
              const isCurrentUser = currentUser.id === member.id
              return (
                <ListItem key={member.id} sx={{ borderRadius: 2, mx: 1, mb: 0.5 }}>
                  <ListItemAvatar>
                    <Box sx={{ position: "relative" }}>
                      <Avatar
                        src={
                          member.profileImage ??
                          "/placeholder.svg"
                        }
                        alt={member.username ?? "Usuario"}
                        sx={{ width: 36, height: 36 }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 0,
                          right: 0,
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          bgcolor: "success.main",
                          border: "2px solid #fff",
                        }}
                      />
                    </Box>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: isCurrentUser ? 600 : 400 }}
                      >
                        {isCurrentUser ?
                          `${member.username} (Tú)` : member.username}
                      </Typography>
                    }
                  />
                </ListItem>
              )
            })}
          </List>
        ) : members.length === 0 ? (
          <Box
            sx={{
              p: 3,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Person sx={{ fontSize: 48, color: "text.secondary" }} />
            <Typography color="text.secondary" variant="body2" textAlign="center">
              Selecciona un canal para ver los miembros activos
            </Typography>
          </Box>
        ) : (
          <>
            {onlineMembers.length > 0 && (
              <>
                <Box sx={{ p: 2, pb: 1 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      letterSpacing: 1,
                    }}
                  >
                    En línea — {onlineMembers.length}
                  </Typography>
                </Box>
                <List sx={{ p: 1, pt: 0 }}>
                  {onlineMembers.map((member) => {
                    const profile = member.profile
                    const isCurrentUser = profile.id === currentUser.id

                    return (
                      <ListItem key={member.id} sx={{ borderRadius: 2, mx: 1, mb: 0.5 }}>
                        <ListItemAvatar>
                          <Box sx={{ position: "relative" }}>
                            <Avatar
                              src={profile.avatarUrl || "/placeholder.svg"}
                              alt={profile.username}
                              sx={{ width: 36, height: 36 }}
                            />
                            <Box
                              sx={{
                                position: "absolute",
                                bottom: 0,
                                right: 0,
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                bgcolor: "success.main",
                                border: "2px solid #fff",
                              }}
                            />
                          </Box>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: isCurrentUser ? 600 : 400 }}>
                                {profile.username}
                                {isCurrentUser && " (Tú)"}
                              </Typography>
                              {member.role === "Admin" && (
                                <AdminPanelSettings sx={{ fontSize: 16, color: "primary.main" }} />
                              )}
                            </Box>
                          }
                          secondary={
                            <Chip
                              label={member.role}
                              size="small"
                              color={member.role === "Admin" ? "primary" : "default"}
                              variant="outlined"
                              sx={{ height: 20, fontSize: "0.6875rem", mt: 0.5 }}
                            />
                          }
                        />
                      </ListItem>
                    )
                  })}
                </List>
              </>
            )}

            {/* Offline Members */}
            {offlineMembers.length > 0 && (
              <>
                <Divider sx={{ mx: 2 }} />
                <Box sx={{ p: 2, pb: 1 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      letterSpacing: 1,
                    }}
                  >
                    Desconectados — {offlineMembers.length}
                  </Typography>
                </Box>
                <List sx={{ p: 1, pt: 0 }}>
                  {offlineMembers.map((member) => {
                    const profile = member.profile
                    const isCurrentUser = profile.id === currentUser.id

                    return (
                      <ListItem key={member.id} sx={{ borderRadius: 2, mx: 1, mb: 0.5, opacity: 0.6 }}>
                        <ListItemAvatar>
                          <Box sx={{ position: "relative" }}>
                            <Avatar
                              src={profile.avatarUrl || "/placeholder.svg"}
                              alt={profile.username}
                              sx={{ width: 36, height: 36 }}
                            />
                            <Circle
                              sx={{
                                position: "absolute",
                                bottom: 0,
                                right: 0,
                                fontSize: 12,
                                color: "grey.400",
                                bgcolor: "white",
                                borderRadius: "50%",
                              }}
                            />
                          </Box>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: isCurrentUser ? 600 : 400 }}>
                                {profile.username}
                                {isCurrentUser && " (Tú)"}
                              </Typography>
                              {member.role === "Admin" && (
                                <AdminPanelSettings sx={{ fontSize: 16, color: "text.secondary" }} />
                              )}
                            </Box>
                          }
                          secondary={
                            <Chip
                              label={member.role}
                              size="small"
                              variant="outlined"
                              sx={{
                                height: 20,
                                fontSize: "0.6875rem",
                                mt: 0.5,
                                opacity: 0.7,
                              }}
                            />
                          }
                        />
                      </ListItem>
                    )
                  })}
                </List>
              </>
            )}
          </>
        )}
      </Box>
    </Paper>
  )
}
